import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  EllipsisVerticalIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  ClockIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

import { 
  PremiumCard,
  PremiumCardHeader, 
  PremiumCardTitle,
  PremiumCardDescription,
  PremiumCardContent,
  PremiumButton,
  PremiumBadge,
  PremiumMetricsCard,
  PremiumConversationCard,
  PremiumNavigation,
  PremiumSidebar,
  NavigationItem
} from '@/components/ui';

// Remove input import to avoid casing conflicts
import { cn } from '@/lib/utils';

// Enhanced interfaces
interface PremiumConversation {
  id: string;
  contact: {
    name: string;
    email: string;
    company?: string;
    avatar?: string;
  };
  lastMessage: {
    text: string;
    timestamp: string;
    sender: 'user' | 'contact' | 'ai';
  };
  status: 'active' | 'priority' | 'escalated' | 'ai-processing' | 'neutral';
  messageCount: number;
  unreadCount?: number;
  tags?: Array<{
    text: string;
    variant?: 'primary' | 'accent' | 'success' | 'warning' | 'destructive';
  }>;
  campaign?: {
    id: string;
    name: string;
  };
  avatar?: {
    src?: string;
    fallback: string;
    status?: 'online' | 'offline' | 'away';
  };
}

interface ConversationStats {
  totalConversations: number;
  activeConversations: number;
  highPriority: number;
  positiveRate: number;
}

const ConversationsPagePremium: React.FC = () => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showRevolutionary, setShowRevolutionary] = useState(false);

  // Mock data with premium structure
  const [stats] = useState<ConversationStats>({
    totalConversations: 28,
    activeConversations: 26,
    highPriority: 0,
    positiveRate: 85
  });

  const [conversations] = useState<PremiumConversation[]>([
    {
      id: '1',
      contact: {
        name: 'Andrew Vercueil',
        email: 'andrewjohnvercueil@gmail.com',
        company: 'Unknown Company'
      },
      lastMessage: {
        text: 'awesome!',
        timestamp: '7:28:44 PM',
        sender: 'contact'
      },
      status: 'active',
      messageCount: 3,
      avatar: {
        fallback: 'AV',
        status: 'online'
      },
      tags: [
        { text: 'Revolutionary', variant: 'primary' },
        { text: 'High Engagement', variant: 'success' }
      ],
      campaign: {
        id: 'campaign_1',
        name: 'Campaign #632457'
      }
    },
    {
      id: '2', 
      contact: {
        name: 'Sarah Johnson',
        email: 'sarah@techflow.com',
        company: 'TechFlow Solutions'
      },
      lastMessage: {
        text: 'Thanks for the detailed proposal. We\'d like to schedule a call to discuss next steps.',
        timestamp: '2 hours ago',
        sender: 'contact'
      },
      status: 'priority',
      messageCount: 12,
      unreadCount: 2,
      avatar: {
        fallback: 'SJ',
        status: 'online'
      },
      tags: [
        { text: 'Hot Lead', variant: 'accent' },
        { text: 'Enterprise', variant: 'primary' }
      ],
      campaign: {
        id: 'campaign_2',
        name: 'Q4 Enterprise Outreach'
      }
    },
    {
      id: '3',
      contact: {
        name: 'Michael Chen',
        email: 'm.chen@innovate.co',
        company: 'Innovate Co'
      },
      lastMessage: {
        text: 'AI is generating a personalized response based on the conversation context...',
        timestamp: 'Just now',
        sender: 'ai'
      },
      status: 'ai-processing',
      messageCount: 8,
      avatar: {
        fallback: 'MC',
        status: 'away'
      },
      tags: [
        { text: 'AI Assisted', variant: 'primary' },
        { text: 'Follow-up', variant: 'warning' }
      ],
      campaign: {
        id: 'campaign_3',
        name: 'AI-Powered Sequences'
      }
    }
  ]);

  const navigationItems: NavigationItem[] = [
    {
      id: 'all',
      label: 'All Conversations',
      icon: <ChatBubbleLeftRightIcon className="w-4 h-4" />,
      isActive: filterStatus === 'all',
      badge: { text: stats.totalConversations.toString(), variant: 'primary' }
    },
    {
      id: 'active',
      label: 'Active',
      icon: <BoltIcon className="w-4 h-4" />,
      isActive: filterStatus === 'active',
      badge: { text: stats.activeConversations.toString(), variant: 'success' }
    },
    {
      id: 'priority',
      label: 'Priority',
      icon: <ArrowTrendingUpIcon className="w-4 h-4" />,
      isActive: filterStatus === 'priority',
      badge: { text: '3', variant: 'accent' }
    },
    {
      id: 'ai-processing',
      label: 'AI Processing',
      icon: <SparklesIcon className="w-4 h-4" />,
      isActive: filterStatus === 'ai-processing',
      badge: { text: '2', variant: 'primary' }
    }
  ];

  const handleConversationSelect = (conversationId: string) => {
    setSelectedConversation(conversationId);
  };

  const handleNavigationSelect = (item: NavigationItem) => {
    setFilterStatus(item.id);
  };

  const filteredConversations = conversations.filter(conv => {
    if (filterStatus === 'all') return true;
    return conv.status === filterStatus;
  });

  const selectedConv = conversations.find(c => c.id === selectedConversation);

  return (
    <div className="h-screen bg-gradient-to-br from-background via-primary/2 to-accent/2 flex overflow-hidden">
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold text-gradient">Premium Conversations</h1>
            <p className="text-xl text-muted-foreground">
              Billion-dollar B2B SaaS aesthetics in action
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PremiumMetricsCard
              title="Total Conversations"
              value={stats.totalConversations}
              change="+5.2% vs last week"
              changeType="increase"
              icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}
              variant="gradient"
              trend="up"
            />
            <PremiumMetricsCard
              title="Active"
              value={stats.activeConversations}
              change="85% positive rate"
              changeType="increase"
              icon={<UserGroupIcon className="w-5 h-5" />}
              variant="success"
              trend="up"
            />
            <PremiumMetricsCard
              title="High Priority"
              value={stats.highPriority}
              change="All handled"
              changeType="increase"
              icon={<ArrowTrendingUpIcon className="w-5 h-5" />}
              variant="accent"
            />
            <PremiumMetricsCard
              title="Avg Response Time"
              value="2.3h"
              change="-15% improvement"
              changeType="increase"
              icon={<ClockIcon className="w-5 h-5" />}
              variant="gradient"
              trend="up"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationsPagePremium; 